import {ActionMethod, ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ACTION_SUBMIT_PROCESS_OUTPUTS_ROWS} from 'notebook/store/VersioningConstants';
import makeSubmitRowActionFunction, {
  getRowAwareLoadingMessage,
} from 'notebook/structured-tables/action-functions/makeSubmitRowActionFunction';
import {performServerSideTableWriteActionWithAudits} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {
  doPerformCreateOutputs,
  ProcessOutputSubmitParams,
} from 'notebook/structured-tables/action-functions/submitProcessOutputsActionFunction';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import {AxisContextMenuActionFunctionParameters} from 'notebook/structured-tables/StructuredTableConstants';
import {outputTableToOutputsForCreateOrUpdate} from 'processes/tables/outputTableToOutputsUtils';
import {
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata,
  ProcessOutputStructuredTable,
} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import submitOutputsWithConfirmation from 'processes/tables/submitOutputsWithConfirmation';

import getDataModsCountForTableSubmission from '../../../processes/tables/getDataModsCountForTableSubmission';
import {getSourceTypeForStructuredTable} from '../util/StructuredTableAnalyticsUtil';

export const SUBMIT_PROCESS_OUTPUTS_ROWS_ACTION_FUNCTION_CONFIG = {
  allowOutsideOfEntry: false,
  onSelectForSubmit: async (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<
      ProcessOutputStructuredTableMetadata,
      ProcessOutputStructuredTableRowMetadata
    >
  ): Promise<void> => {
    const entryId = actionFunctionParams.entryOrTemplateId!;
    const processOutputStructuredTable: ProcessOutputStructuredTable = actionFunctionParams.structuredTable;
    const selectionWithExclusiveEnd = {
      start: actionFunctionParams.selectionRange!.minIndex,
      end: actionFunctionParams.selectionRange!.maxIndex + 1,
    };
    const pertinentComputedTable = selectionWithExclusiveEnd
      ? actionFunctionParams.computedTable.slice(
          selectionWithExclusiveEnd.start,
          selectionWithExclusiveEnd.end
        )
      : actionFunctionParams.computedTable;
    const outputs = outputTableToOutputsForCreateOrUpdate({
      table: processOutputStructuredTable,
      evaluatedTable: pertinentComputedTable,
      creationOriginIds: Array(pertinentComputedTable.length).fill(entryId),
      sourceType: `entry_${getSourceTypeForStructuredTable(processOutputStructuredTable)}_direct`,
      actionOrigin: ActionOrigin.NOTEBOOK_ENTRY_WORKFLOW_OUTPUT_TABLE,
      actionMethod: ActionMethod.BUTTON_CLICK,
      debugInfo: {entryId},
    });

    await submitOutputsWithConfirmation({
      outputs,
      onRequestSubmit: async (outputSubmitParams: ProcessOutputSubmitParams) => {
        await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.ProcessOutputSubmitResponse>({
          entryId,
          loadingScreenMessage: getRowAwareLoadingMessage(
            selectionWithExclusiveEnd,
            'Submitting Outputs For Rows',
            'Submitting Output For Row'
          ),
          autoSaverAction: ACTION_SUBMIT_PROCESS_OUTPUTS_ROWS,
          doPerformAction: ({updateStatus, dataModsHeader, allRowsSubmitted}) =>
            doPerformCreateOutputs(
              entryId,
              processOutputStructuredTable,
              actionFunctionParams.computedTable,
              actionFunctionParams.actionFunctionContext,
              outputSubmitParams,
              selectionWithExclusiveEnd,
              updateStatus,
              dataModsHeader,
              allRowsSubmitted
            ),
          dataModsItemFn: (
            structuredTables,
            structuredTableAdapters,
            computedTables,
            tableSubmissionUpdates
          ) => getDataModsCountForTableSubmission(structuredTables, tableSubmissionUpdates),
          analyticsActionForPerfTracking: 'submit_process_output_structured_table_rows',
          structuredTables: [processOutputStructuredTable],
          structuredTableAdapters: [actionFunctionParams.structuredTableAdapter],
          computedTables: [actionFunctionParams.computedTable],
          tables: actionFunctionParams.table ? [actionFunctionParams.table] : [],
          selection: selectionWithExclusiveEnd,
        });
      },
    });
  },
};

export default makeSubmitRowActionFunction<
  ProcessOutputStructuredTableMetadata,
  ProcessOutputStructuredTableRowMetadata
>(SUBMIT_PROCESS_OUTPUTS_ROWS_ACTION_FUNCTION_CONFIG);
